import axios from 'axios';
import API_CONFIG from '../config';

/** Clear stored admin session (token + user profile). */
export function clearAdminSession() {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
}

function redirectToAdminLogin(sessionExpired = false) {
  const path = window.location.pathname || '';
  if (path.startsWith('/login')) return;
  const suffix = sessionExpired ? '?session=expired' : '';
  window.location.replace(`/login${suffix}`);
}

// Create axios instance
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // If data is FormData, remove Content-Type to let Axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors — auto logout when the API rejects the session
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = String(error.config?.url || '');
    const isLoginRequest = url.includes('/auth/admin-login');
    if (status === 401 && !isLoginRequest) {
      const hadSession = !!localStorage.getItem('admin_token');
      clearAdminSession();
      if (hadSession) {
        redirectToAdminLogin(true);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post(API_CONFIG.ADMIN.LOGIN, { email, password }),
  me: () => api.get('/auth/me'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get(API_CONFIG.ADMIN.DASHBOARD),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get(API_CONFIG.ADMIN.USERS, { params }),
  getById: (id) => api.get(`${API_CONFIG.ADMIN.USERS}/${id}`),
  create: (data) => api.post(API_CONFIG.ADMIN.USERS, data),
  update: (id, data) => api.put(`${API_CONFIG.ADMIN.USERS}/${id}`, data),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.USERS}/${id}`),
  suspend: (id) => api.post(`${API_CONFIG.ADMIN.USERS}/${id}/suspend`),
  unsuspend: (id) => api.post(`${API_CONFIG.ADMIN.USERS}/${id}/unsuspend`),
};

// Customers API
export const customersAPI = {
  getAll: (params) => api.get(API_CONFIG.ADMIN.CUSTOMERS, { params }),
  getById: (id) => api.get(`${API_CONFIG.ADMIN.CUSTOMERS}/${id}`),
  update: (id, data) => api.put(`${API_CONFIG.ADMIN.CUSTOMERS}/${id}`, data),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.CUSTOMERS}/${id}`),
  suspend: (id) => api.post(`${API_CONFIG.ADMIN.CUSTOMERS}/${id}/suspend`),
  unsuspend: (id) => api.post(`${API_CONFIG.ADMIN.CUSTOMERS}/${id}/unsuspend`),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get(API_CONFIG.ADMIN.PRODUCTS, { params }),
  getById: (id) => api.get(`${API_CONFIG.ADMIN.PRODUCTS}/${id}`),
  create: (data) => api.post(API_CONFIG.ADMIN.PRODUCTS, data),
  update: (id, data) => api.put(`${API_CONFIG.ADMIN.PRODUCTS}/${id}`, data),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.PRODUCTS}/${id}`),
  approve: (id) => api.post(`${API_CONFIG.ADMIN.PRODUCTS}/${id}/approve`),
  reject: (id) => api.post(`${API_CONFIG.ADMIN.PRODUCTS}/${id}/reject`),
  duplicate: (id) => api.post(`${API_CONFIG.ADMIN.PRODUCTS}/${id}/duplicate`),
};

// CSV exports (blob responses)
export const exportsAPI = {
  customers: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.CUSTOMERS, { params, responseType: 'blob' }),
  users: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.USERS, { params, responseType: 'blob' }),
  products: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.PRODUCTS, { params, responseType: 'blob' }),
  closet: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.CLOSET, { params, responseType: 'blob' }),
  orders: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.ORDERS, { params, responseType: 'blob' }),
  abandonedCheckouts: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.ABANDONED_CHECKOUTS, {
      params,
      responseType: 'blob',
    }),
  reservations: (params) =>
    api.get(API_CONFIG.ADMIN.EXPORT.RESERVATIONS, {
      params,
      responseType: 'blob',
    }),
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get(API_CONFIG.ADMIN.ORDERS, { params }),
  getById: (id) => api.get(`${API_CONFIG.ADMIN.ORDERS}/${id}`),
  update: (id, data) => api.put(`${API_CONFIG.ADMIN.ORDERS}/${id}`, data),
  shipWithDhl: (id) => api.post(`${API_CONFIG.ADMIN.ORDERS}/${id}/ship-dhl`),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.ORDERS}/${id}`),
};

export const abandonedCheckoutsAPI = {
  getAll: (params) => api.get(API_CONFIG.ADMIN.ABANDONED_CHECKOUTS, { params }),
  getById: (id) => api.get(`${API_CONFIG.ADMIN.ABANDONED_CHECKOUTS}/${id}`),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.ABANDONED_CHECKOUTS}/${id}`),
  bulkDelete: (ids) =>
    api.post(`${API_CONFIG.ADMIN.ABANDONED_CHECKOUTS}/bulk-delete`, { ids }),
};

export const reservationsAPI = {
  getAll: (params) => api.get(API_CONFIG.ADMIN.RESERVATIONS, { params }),
  getById: (id) => api.get(`${API_CONFIG.ADMIN.RESERVATIONS}/${id}`),
  complete: (id, data) => api.post(`${API_CONFIG.ADMIN.RESERVATIONS}/${id}/complete`, data),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.RESERVATIONS}/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get(API_CONFIG.ADMIN.CATEGORIES),
  create: (data) => api.post(API_CONFIG.ADMIN.CATEGORIES, data),
  update: (id, data) => api.put(`${API_CONFIG.ADMIN.CATEGORIES}/${id}`, data),
  delete: (id) => api.delete(`${API_CONFIG.ADMIN.CATEGORIES}/${id}`),
};

// Category brands API
export const brandsAPI = {
  getAll: (params) => api.get('/admin/brands', { params }),
  get: (id) => api.get(`/admin/brands/${id}`),
  create: (data) => api.post('/admin/brands', data),
  update: (id, data) => api.put(`/admin/brands/${id}`, data),
  delete: (id) => api.delete(`/admin/brands/${id}`),
};

// Product settings API
export const productSettingsAPI = {
  get: () => api.get('/admin/product-settings'),
  update: (data) => api.put('/admin/product-settings', data),
};

// Mobile app minimum versions / force update (admin)
export const mobileVersionSettingsAPI = {
  get: () => api.get('/admin/mobile-version-settings'),
  update: (data) => api.put('/admin/mobile-version-settings', data),
};

// SMTP + transactional email templates (admin)
export const mailSettingsAPI = {
  get: () => api.get('/admin/mail-settings'),
  update: (data) => api.put('/admin/mail-settings', data),
};

// Admin currencies API
export const adminCurrenciesAPI = {
  getAll: () => api.get('/admin/currencies'),
  create: (data) => api.post('/admin/currencies', data),
  update: (id, data) => api.put(`/admin/currencies/${id}`, data),
  delete: (id) => api.delete(`/admin/currencies/${id}`),
};

export const adminShippingRatesAPI = {
  getAll: () => api.get('/admin/shipping-rates'),
  create: (data) => api.post('/admin/shipping-rates', data),
  update: (id, data) => api.put(`/admin/shipping-rates/${id}`, data),
  delete: (id) => api.delete(`/admin/shipping-rates/${id}`),
};

export const adminCountriesAPI = {
  getAll: () => api.get('/admin/countries'),
  update: (id, data) => api.put(`/admin/countries/${id}`, data),
};

export const adminLocationsAPI = {
  getAll: (params) => api.get('/admin/locations', params ? { params } : undefined),
  getById: (id) => api.get(`/admin/locations/${id}`),
  create: (data) => api.post('/admin/locations', data),
  update: (id, data) => api.put(`/admin/locations/${id}`, data),
  delete: (id) => api.delete(`/admin/locations/${id}`),
};

// Admin home banner API
export const adminHomeBannerAPI = {
  get: () => api.get('/admin/home-banner'),
  update: (data) => api.put('/admin/home-banner', data),
};

// Blog posts (admin)
export const blogsAPI = {
  getAll: (params) => api.get('/admin/blogs', params ? { params } : undefined),
  getById: (id) => api.get(`/admin/blogs/${id}`),
  create: (data) => api.post('/admin/blogs', data),
  update: (id, data) => api.put(`/admin/blogs/${id}`, data),
  delete: (id) => api.delete(`/admin/blogs/${id}`),
};

export const styleCollectionsAPI = {
  getAll: () => api.get('/admin/style-collections'),
  getById: (id) => api.get(`/admin/style-collections/${id}`),
  create: (data) => api.post('/admin/style-collections', data),
  update: (id, data) => api.put(`/admin/style-collections/${id}`, data),
  delete: (id) => api.delete(`/admin/style-collections/${id}`),
  syncProducts: (id, products) =>
    api.post(`/admin/style-collections/${id}/products`, { products }),
};

// Expo push notifications (admin → users’ devices)
export const pushNotificationsAPI = {
  recipients: (params) => api.get('/admin/notifications/recipients', { params }),
  send: (payload) => api.post('/admin/notifications/send', payload),
  previewUser: (id) => api.get(`/admin/notifications/users/${id}/preview`),
};

// Conditions API (public — active only, localized by Accept-Language)
export const conditionsAPI = {
  getAll: () => api.get('/conditions'),
};

/** Admin: all conditions + edit translations */
export const adminConditionsAPI = {
  getAll: () => api.get('/admin/conditions'),
  update: (id, data) => api.put(`/admin/conditions/${id}`, data),
};

// Colors API (admin CRUD)
export const colorsAPI = {
  getAll: () => api.get('/admin/colors'),
  create: (data) => api.post('/admin/colors', data),
  update: (id, data) => api.put(`/admin/colors/${id}`, data),
  delete: (id) => api.delete(`/admin/colors/${id}`),
};

/** Public active locations (storefront / product forms). */
export const locationsAPI = {
  getAll: () => api.get('/locations'),
};

// Currencies API
export const currenciesAPI = {
  getAll: () => api.get('/currencies'),
};

// Upload API
/** Stay under typical 8MB PHP post_max_size when server limits are not raised yet. */
const UPLOAD_CHUNK_MAX_BYTES = 6 * 1024 * 1024;
const UPLOAD_CHUNK_MAX_FILES = 8;

function chunkFilesForUpload(files) {
  const list = Array.from(files || []);
  if (list.length === 0) return [];

  const chunks = [];
  let current = [];
  let currentSize = 0;

  for (const file of list) {
    const size = file.size || 0;
    const exceedsSize = current.length > 0 && currentSize + size > UPLOAD_CHUNK_MAX_BYTES;
    const exceedsCount = current.length >= UPLOAD_CHUNK_MAX_FILES;

    if (exceedsSize || exceedsCount) {
      chunks.push(current);
      current = [];
      currentSize = 0;
    }
    current.push(file);
    currentSize += size;
  }

  if (current.length) chunks.push(current);
  return chunks;
}

export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/image', formData);
  },
  /** Batch upload — splits into chunks if needed; extended timeout for large galleries. */
  uploadImages: async (files, options = {}) => {
    const list = Array.from(files || []);
    const chunks = chunkFilesForUpload(list);
    const timeout = options.timeout ?? 300000;

    if (chunks.length === 0) {
      return { data: { images: [], errors: [], uploaded: 0, failed: 0 } };
    }

    const postChunk = (chunk) => {
      const formData = new FormData();
      chunk.forEach((file) => formData.append('images[]', file));
      return api.post('/upload/images', formData, { timeout });
    };

    if (chunks.length === 1) {
      return postChunk(chunks[0]);
    }

    const combined = { images: [], errors: [], uploaded: 0, failed: 0 };
    let offset = 0;

    for (const chunk of chunks) {
      const { data } = await postChunk(chunk);
      (data.images || []).forEach((row) => {
        combined.images.push({ index: offset + row.index, image_url: row.image_url });
      });
      (data.errors || []).forEach((row) => {
        combined.errors.push({ index: offset + row.index, message: row.message });
      });
      combined.uploaded += data.uploaded || 0;
      combined.failed += data.failed || 0;
      offset += chunk.length;
    }

    return { data: combined };
  },
};

// Closet API
export const closetAPI = {
  getInfluencers: (params) => api.get(`${API_CONFIG.ADMIN.CLOSET}/influencers`, { params }),
  getInfluencerProducts: (id, params) => api.get(`${API_CONFIG.ADMIN.CLOSET}/influencers/${id}/products`, { params }),
  createInfluencer: (data) => api.post(`${API_CONFIG.ADMIN.CLOSET}/influencers`, data),
  updateInfluencer: (id, data) => api.put(`${API_CONFIG.ADMIN.CLOSET}/influencers/${id}`, data),
  deleteInfluencer: (id) => api.delete(`${API_CONFIG.ADMIN.CLOSET}/influencers/${id}`),
  getMyBankDetails: () => api.get(`${API_CONFIG.ADMIN.CLOSET}/me/bank-details`),
  updateMyBankDetails: (data) => api.put(`${API_CONFIG.ADMIN.CLOSET}/me/bank-details`, data),
};

// Permissions API
export const permissionsAPI = {
  getRoles: () => api.get(`${API_CONFIG.ADMIN.PERMISSIONS}/roles`),
  getPermissions: () => api.get(`${API_CONFIG.ADMIN.PERMISSIONS}/permissions`),
  getUserRoles: (userId) => api.get(`${API_CONFIG.ADMIN.PERMISSIONS}/users/${userId}`),
  assignRole: (userId, roleId) => api.post(`${API_CONFIG.ADMIN.PERMISSIONS}/users/${userId}/assign-role`, { role_id: roleId }),
  removeRole: (userId, roleId) => api.post(`${API_CONFIG.ADMIN.PERMISSIONS}/users/${userId}/remove-role`, { role_id: roleId }),
  assignPermission: (userId, permissionId) => api.post(`${API_CONFIG.ADMIN.PERMISSIONS}/users/${userId}/assign-permission`, { permission_id: permissionId }),
  removePermission: (userId, permissionId) => api.post(`${API_CONFIG.ADMIN.PERMISSIONS}/users/${userId}/remove-permission`, { permission_id: permissionId }),
};

export const auctionDepositsAPI = {
  getAll: (params) => api.get('/admin/auction-deposits', { params }),
  review: (depositId, data) => api.patch(`/admin/auction-deposits/${depositId}`, data),
};

export const auctionsAPI = {
  getAll: (params) => api.get('/admin/auctions', { params }),
  getById: (id) => api.get(`/admin/auctions/${id}`),
  create: (data) => api.post('/admin/auctions', data),
  update: (id, data) => api.put(`/admin/auctions/${id}`, data),
  toggleVisibility: (id, data) => api.patch(`/admin/auctions/${id}/visibility`, data),
  start: (id) => api.post(`/admin/auctions/${id}/start`),
  nextItem: (id) => api.post(`/admin/auctions/${id}/next-item`),
  extendItem: (id) => api.post(`/admin/auctions/${id}/extend-item`),
  markUnsold: (id) => api.post(`/admin/auctions/${id}/mark-unsold`),
  end: (id) => api.post(`/admin/auctions/${id}/end`),
  getResults: (id) => api.get(`/admin/auctions/${id}/results`),
  getBids: (id, params) => api.get(`/admin/auctions/${id}/bids`, { params }),
};

export default api;

